/**
 * Picker → ADF storage adapter.
 *
 * If the hex emitted by the color-picker matches a shade in the active brand
 * palette, persist the CSS-var reference for that shade (e.g.
 * `"var(--hub-color-primary-300)"`) so the value tracks future brand-color
 * edits via the browser's CSS cascade — no resolver call, no JS palette
 * lookup at render time. Otherwise persist the raw hex (frozen literal).
 *
 * Pure case-insensitive lookup, no normalisation — relies on the picker
 * emitting hex in the same casing the palette uses (`csm-colour-theme-generator`
 * outputs lowercase). Pure, synchronous, no side effects → SSR-safe.
 */
import { HUB_PRIMARY_COLOR_CSS_VAR } from '../../constants';

import { HUB_COLOR_CSS_VARS } from './hub-css-vars';
import type { HubBrandPalette, PaletteRef } from './types';

export type ToAdfColorOptions = {
	/** `true` for un-entitled (free / standard) tenants — see below. */
	isBrandPaletteLocked?: boolean;
	/** The tenant's live brand colour, i.e. `branding.colors.primary`. */
	primaryColor?: string;
};

export const toAdfColor = (
	hex: string,
	palette: HubBrandPalette,
	opts?: ToAdfColorOptions,
): string => {
	if (opts?.isBrandPaletteLocked) {
		// Locked tenants have no palette to track — the picker offers exactly
		// one brand swatch (the admin's primary) plus custom hex. Anchor a
		// primary pick to `--hub-primary-color`, which `HubThemingProvider`
		// keeps pointed at the live brand colour, so the element keeps
		// following future brand-colour edits.
		//
		// The palette path below would *look* like it works — `generateColors`
		// substitutes the brand hex into whichever ramp slot is
		// luminance-closest, so the pick does match a shade — but that slot
		// index is a function of the brand colour. Re-brand and the persisted
		// `--hub-color-primary-<N>` resolves to an unrelated tint of the new
		// hue instead of the new brand colour. And when the primary isn't a
		// hex `generateThemeBranding` can parse, it appears nowhere in the
		// ramp and the pick is persisted as a bare literal with no var at all.
		if (opts.primaryColor && hex.toLowerCase() === opts.primaryColor.toLowerCase()) {
			return `var(${HUB_PRIMARY_COLOR_CSS_VAR}, ${hex})`;
		}
		// Anything else is a custom colour: freeze it, so an un-entitled
		// tenant can't persist a palette-shade ref via a stale current value.
		return hex;
	}

	const lower = hex.toLowerCase();
	for (const [ref, shadeHex] of Object.entries(palette) as Array<[PaletteRef, string]>) {
		if (shadeHex.toLowerCase() === lower) {
			// Hex fallback after the CSS var name lets the value still resolve
			// to a usable color when the wrapper isn't in scope (e.g. shared
			// extensions rendered in Confluence Company Hub, or color helpers
			// like `extractColorCode` that look up the var on `documentElement`
			// rather than the wrapper). The browser uses the wrapper's live
			// value when the var is defined (so brand-color edits still track
			// inside the hub), and falls back to this snapshot hex otherwise.
			return `var(${HUB_COLOR_CSS_VARS[ref]}, ${shadeHex})`;
		}
	}
	return hex;
};
